import React, { useState } from 'react'
import { Table, Tooltip, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { Link } from 'react-router-dom'
import type { LinkProps } from 'react-router-dom'
import moment from 'moment'
import '../../styles/flagTooltip.css'
import './tripTable.css'

const { Text } = Typography

export interface Trip {
  brukare_from: { personnummer: string; förnamn: string | null; efternamn: string | null }
  brukare_to: { personnummer: string; förnamn: string | null; efternamn: string | null }
  start: string
  end: string
  utförare_from: string | null
  utförare_to: string | null
}

const getBrukareName = (b: Trip['brukare_from']) => `${b.förnamn ?? ''} ${b.efternamn ?? ''}`.trim() || 'Okänd brukare'

// Avkortning per fält: brukare 16 tecken, utförare 14 tecken. Kolumnbredderna nedan följer detta.
const BRUKARE_MAX = '16ch'
const UTFORARE_MAX = '14ch'
const cellDivider = { borderRight: '1px solid #f0f0f0' }

const inlineEllipsis = (maxWidth: string): React.CSSProperties => ({
  display: 'inline-block',
  maxWidth,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  verticalAlign: 'bottom',
})

// Ett avkortat fält (brukare som länk, eller utförare som text). Visar hela texten i en
// flag-tooltip när den är klippt (mätt på hover, ett fält i taget — ingen render-mätning/observer).
const TruncField = ({ text, to, maxWidth }: { text: string; to?: LinkProps['to']; maxWidth: string }) => {
  const [truncated, setTruncated] = useState(false)
  const measure = (e: React.MouseEvent<HTMLElement>) => setTruncated(e.currentTarget.scrollWidth > e.currentTarget.clientWidth)
  const content = to ? (
    <Link to={to} onMouseEnter={measure} style={inlineEllipsis(maxWidth)}>
      {text}
    </Link>
  ) : (
    <Text onMouseEnter={measure} style={inlineEllipsis(maxWidth)}>
      {text}
    </Text>
  )
  return (
    <Tooltip title={truncated ? text : ''} overlayClassName="flag-tooltip" placement="topLeft">
      {content}
    </Tooltip>
  )
}

// Ett besök på EN rad: {label}: klockslag · brukare (länk) · utförare. Brukare och utförare avkortas var för sig. Enhetlig färg/storlek (brukaren är länk).
const BesökLine = ({ label, time, brukare, utförare }: { label: string; time: string; brukare: Trip['brukare_from']; utförare: string | null }) => {
  const namn = getBrukareName(brukare)
  const utf = utförare ?? 'Okänd utförare'
  return (
    <span style={{ whiteSpace: 'nowrap' }}>
      <Text>{`${label}: ${moment(time).format('HH:mm')} · `}</Text>
      <TruncField text={namn} to={{ pathname: `/app/brukare/id=${brukare.personnummer}&namn=${namn}` }} maxWidth={BRUKARE_MAX} />
      <Text> · </Text>
      <TruncField text={utf} maxWidth={UTFORARE_MAX} />
    </span>
  )
}

interface TripTableProps {
  trips: Trip[]
}

// Återanvändbar tabell för restids-/överlappningsflaggor (flagga 10, ev. 9 framöver). Inga kolumnhuvuden, en rad per överlappning, vertikala avdelare mellan kolumnerna.
const TripTable = ({ trips }: TripTableProps) => {
  const columns: ColumnsType<Trip> = [
    {
      title: 'Datum',
      key: 'datum',
      onCell: () => ({ style: cellDivider }),
      render: (_, trip) => <span style={{ whiteSpace: 'nowrap' }}>{moment(trip.start).format('YYYY-MM-DD')}</span>,
    },
    {
      title: 'Besök som slutade',
      key: 'from',
      onCell: () => ({ style: cellDivider }),
      render: (_, trip) => <BesökLine label="Slut" time={trip.start} brukare={trip.brukare_from} utförare={trip.utförare_from} />,
    },
    {
      title: 'Besök som startade',
      key: 'to',
      render: (_, trip) => <BesökLine label="Start" time={trip.end} brukare={trip.brukare_to} utförare={trip.utförare_to} />,
    },
  ]

  return <Table<Trip> className="trip-table" columns={columns} dataSource={trips} rowKey={(_, i) => String(i)} size="small" showHeader={false} pagination={false} />
}

export default TripTable
